import React, { useState, useEffect } from 'react';
import '../Coaching/academyplayerlist.css';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const AcademyPlayerList = () => {
  const { userData } = useUser();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const academyIdFromQuery = queryParams.get("academyId");
  const batchIdFromQuery = queryParams.get("batchId");

  const { academy } = location.state || {};
  const { batchId: batchIdFromState } = location.state || {};
  const academyId = academy?.id || academyIdFromQuery;
  const batchId = batchIdFromState || batchIdFromQuery;

  const { selectedCenter, setSelectedCenter } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [players, setPlayers] = useState([]);
  const token = localStorage.getItem('auth-token');

  const centerToUse = academy && academy.id ? academy.id : selectedCenter;
  const fetchPlayersData = async () => {
    setLoading(true);
    setError(false)
    try {
      let url;
      if (batchId) {
        url = `${process.env.REACT_APP_API_BATCH_PLAYERS}/${batchId}/players`;
      }
      else if (centerToUse) {
        url = `${process.env.REACT_APP_API_ACADEMY_PLAYERS}/${centerToUse}/players`;
      }
      else {
        throw new Error('No batchId or academyId provided.');
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch players data');
      }

      const result = await response.json();
      if (result.success) {
        const updatedPlayers = result.data.players.map((player) => {
          const playerId = player.id;
          const playerDetails = {
            id: playerId,
            user: player.user?.id || '-',
            name: player.user?.name || '-',
            parentName: result.data.parentPlayer?.[playerId] || '-',
            parentMobile: result.data.parentPlayerMobileNumber?.[playerId] || '-',
            category: player.playerCategoryType || '-',
            level: player.playerLevelType || '-',
            mobile: player.user?.mobile_number || '-',
            dob: formatDate(player.dateOfBirth) || '-',
            isEnrolled: result.data.isEnrollmentMap?.[playerId] || false,
            playingMember: result.data.playingMember?.[playerId] || false,
          };
          return playerDetails;
        });

        setPlayers(updatedPlayers);
      } else {
        setError('No players found.');
      }
    } catch (error) {

      setError('Error fetching data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (batchId || centerToUse) {
      fetchPlayersData();
    } else {
      setError('No valid batchId or academyId provided.');
      setLoading(false);
    }
  }, [academy, selectedCenter, batchId]);

  const formatDate = (dateString) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return '-';
    }
    const dateOnly = dateString.split('T')[0];

    return dateOnly;
  };

  const headers = [
    { label: "Name", key: "name" },
    { label: "Parent Name", key: "parentName" },
    { label: "Parent Mobile", key: "parentMobile" },
    { label: "Category", key: "category" },
    { label: "Level", key: "level" },
    { label: "Mobile", key: "mobile" },
    { label: "Date of Birth", key: "dob" },
    { label: "Is Enrolled", key: "isEnrolled" },
    { label: "Playing Member", key: "playingMember" },
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    parentName: '',
    parentMobile: '',
    category: '',
    level: '',
    mobile: '',
    dob: '',
    isEnrolled: false,
    playingMember: false,
  });

  const [dropdownOpen, setDropdownOpen] = useState(null);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name === 'isEnrolled' || name === 'playingMember' ? e.target.checked : value });
  };

  const filteredPlayers = players.filter((player) =>
    Object.values(player)
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const toggleDropdown = (playerId, e) => {
    e.stopPropagation();
    if (dropdownOpen === playerId) {
      return setDropdownOpen(null);
    }
    const { bottom, left } = e.currentTarget.getBoundingClientRect();
    setDropdownPos({
      top: bottom + window.scrollY,
      left: left + window.scrollX,
    });
    setDropdownOpen(playerId);
  };


  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "ascending",
  });

  const handleSort = (columnKey) => {
    let direction = "ascending";
    if (sortConfig.key === columnKey && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;

    return [...data].sort((a, b) => {
      let valueA = a[key] ?? "";
      let valueB = b[key] ?? "";

      if (typeof valueA === "string" && typeof valueB === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return direction === "ascending" ? valueA - valueB : valueB - valueA;
      }

      if (typeof valueA === "number" && typeof valueB === "number") {
        return direction === "ascending" ? valueA - valueB : valueB - valueA;
      }

      return direction === "ascending" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  };


  const sortedData = sortData(filteredPlayers);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const handleEdit = (event, playerId) => {
    const url = `/createPlayer?academyId=${academy?.id || selectedCenter}&playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };


  const handleDetails = (event, playerId) => {
    const selectedPlayer = players.find(player => player.id === playerId);
    const userId = selectedPlayer ? selectedPlayer.user : null;
    const academyId = academy?.id ? Number(academy.id) : Number(selectedCenter);

    const url = `/createPlayer?academyId=${academyId}&userId=${userId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handleDelete = async (playerId) => {
    const confirmation = await Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!',
      cancelButtonText: 'Cancel'
    });

    if (!confirmation.isConfirmed) {
      return;
    }
    try {
      const url = `${process.env.REACT_APP_API_REMOVE_PLAYER_LIST}?playerId=${playerId}&academyId=${selectedCenter}`;


      const response = await fetch(url, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to remove player from academy");
      }

      const result = await response.json();

      setPlayers((prevPlayers) => prevPlayers.filter(player => player.id !== playerId));
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Player removed successfully!',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (error) {

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Something went wrong while removing the player!',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleShop = (event, playerId) => {
    const url = `/shop?academyId=${academy?.id || selectedCenter}&playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  useEffect(() => {
    const closeOnScroll = () => setDropdownOpen(null);

    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, []);
  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('.custom-dropdown-player');
      if (dropdown && !dropdown.contains(event.target) && !event.target.closest('.dots-btn-player')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleCreateplayer = () => navigate('/createNewPlayer');

  const cancelAction = () => {
    setIsModalOpen(false);
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };



  const totalEntries = filteredPlayers.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;
  const handlePlayerAttendance = (event, playerId) => {
    const url = `/playerAttendance?playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <div className="enrollment-container">

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <button
          className="create-button-new"
          onClick={handleCreateplayer}
        >
          <span className="create-button__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="23"
              height="23"
              className="create-button__svg"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="white"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="create-button__label">
            Create Player
          </span>
        </button>
      </div>

      <div className="items-and-dues">
        <select
          className="items-per-page-select"
          value={rowsPerPage}
          onChange={handleItemsPerPageChange}
        >
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>

        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button className="tt-action-btn" onClick={fetchPlayersData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>



      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredPlayers.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="payment-list-scroll-container" style={{ overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
              {hasPrivilege(userData, 'COACHING', 'ACADEMY_PLAYER_LIST_MODIFY') && (<th>Action</th>)}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((player) => (
              <tr key={player.id}>
                <td>{player.name}</td>
                <td>{player.parentName}</td>
                <td>{player.parentMobile}</td>
                <td>{player.category}</td>
                <td>{player.level}</td>
                <td>{player.mobile}</td>
                <td>{player.dob}</td>
                <td>{player.isEnrolled ? 'Yes' : 'No'}</td>
                <td>{player.playingMember ? 'Yes' : 'No'}</td>
                {hasPrivilege(userData, 'COACHING', 'ACADEMY_PLAYER_LIST_MODIFY') && (
                  <td className="sport-actions-column">
                    <button
                      className="dots-btn-player"
                      onClick={(e) => toggleDropdown(player.id, e)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '24px',
                      }}
                    >
                      &#x22EE;
                    </button>

                    {dropdownOpen === player.id && (
                      <div
                        className="custom-dropdown-player"
                        style={{
                          position: 'absolute',
                          top: dropdownPos.top,
                          left: dropdownPos.left,
                          zIndex: 9999,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                          minWidth: '150px',
                        }}
                      >
                        <button
                          className="dropdown-item"
                          onClick={(event) => handleDetails(event, player.id)}
                        >
                          View Details
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={(event) => handlePlayerAttendance(event, player.id)}
                        >
                          Attendance
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={(event) => handleEdit(event, player.id)}
                        >
                          Edit
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => handleDelete(player.id)}
                        >
                          Remove
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={(event) => handleShop(event, player.id)}
                        >
                          Shop
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-btn" onClick={cancelAction}>&times;</button>
            <div>
              <p>
                Are you sure you want to delete?
              </p>
              <div className="modal-actions">
                <button className="confirm-btn-academy" >
                  Confirm Disable
                </button>
                <button className="cancel-btn-academy" onClick={cancelAction}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademyPlayerList;
